// Vaste volgorde voor de adressenlijst van een wervingsactie: straat op
// alfabet, dan huisnummer als getal, dan toevoeging.
//
// Nodig omdat de groepen eerst de volgorde kregen waarin PDOK de adressen
// aanlevert. Vinkte je het eerste adres van een straat aan, dan kreeg die
// straat de plek van zijn eerstvolgende adres in die PDOK-volgorde, en sprong
// hij in Zutphen-Zuidwijken van plek 1 naar plek 13. Wie straat voor straat
// werkt, raakte zo na elk vinkje zijn straat kwijt.
//
// AI-CONTEXT: See docs/features/wervingsacties-registratie.md

const OVERIG = 'Overig'

/**
 * @typedef {object} WervingsAdres
 * @property {string} [Straatnaam] - Straat uit PDOK
 * @property {string|number} [Huisnummer] - Huisnummer, als tekst of getal
 * @property {string} [HuisnummerToevoeging] - Bijvoorbeeld 'A' of 'bis'
 */

/**
 * @param {string|number|undefined} nr
 * @returns {number} Het huisnummer als getal; 0 als het ontbreekt
 */
const toNumber = (nr) => parseInt(nr, 10) || 0

/**
 * Vergelijkt twee adressen op straat, huisnummer en toevoeging.
 * Bruikbaar als `Array.prototype.sort`-vergelijking.
 *
 * @param {WervingsAdres} a
 * @param {WervingsAdres} b
 * @returns {number}
 */
const compareAddresses = (a, b) =>
    (a.Straatnaam || '').localeCompare(b.Straatnaam || '', 'nl') ||
    toNumber(a.Huisnummer) - toNumber(b.Huisnummer) ||
    (a.HuisnummerToevoeging || '').localeCompare(b.HuisnummerToevoeging || '', 'nl')

/**
 * Groepeert adressen per straat, in een vaste volgorde. Adressen zonder
 * straatnaam komen als laatste groep onder 'Overig'.
 *
 * @template {WervingsAdres} T
 * @param {Array<T>} addresses
 * @returns {Array<[string, Array<T>]>} Paren van straatnaam en adressen
 */
const groupAddressesByStreet = (addresses) => {
    const map = new Map()
    for (const a of [...addresses].sort(compareAddresses)) {
        const street = a.Straatnaam || OVERIG
        if (!map.has(street)) map.set(street, [])
        map.get(street).push(a)
    }

    // Lege straatnamen sorteren vóór de letters; 'Overig' hoort onderaan.
    const groups = Array.from(map.entries())
    const overig = groups.filter(([street]) => street === OVERIG)
    return [...groups.filter(([street]) => street !== OVERIG), ...overig]
}

export { compareAddresses, groupAddressesByStreet }
